// FILE: src/pages/ManageUsersPage.jsx
//
// Manage Users — UI redesign (design language shared with ShiftsPage).
//
//   - Geist / Geist Mono typography, CSS fully scoped under `.mupg-root`
//   - Sticky page-top (title + count, Add User action, search filter bar)
//   - Users as a compact table: role chips, assigned-account pills,
//     mono timestamps, 3-dot row menu (hidden for Admin rows — unchanged)
//   - Add / Edit / Delete dialogs re-skinned but powered by MUI Dialog,
//     Select and Menu underneath for behavior + accessibility.
//
// IMPORTANT — what we preserved from the previous file:
//   - All API calls byte-identical:
//       GET  /users?search=${search}            (search-triggered refetch)
//       GET  /accounts?limit=1000000000
//       POST /users                              (adminPassword re-auth)
//       GET  /users?search=${newUser.username}   (email blur uniqueness check)
//       PUT  /users/${editUser._id}              (adminPassword re-auth)
//       DELETE /users/${deleteUserId}            (adminPassword in body)
//   - All handlers, validation and dialog flows exactly as they were —
//     only their visuals changed.
//   - 3-dot menu still hidden when user.role === "Admin".
//   - fullScreen dialogs on small screens (Add / Edit) kept.

import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Menu,
} from "@mui/material";
import API from "../api";

// ----------------------------------------
// CSS — fully scoped under `.mupg-root`.
// ----------------------------------------
const MANAGE_USERS_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;450;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

.mupg-root {
  --mupg-primary: #1E90FF;
  --mupg-primary-soft: #E8F3FF;
  --mupg-warn: #FFC107;
  --mupg-warn-soft: #FFF7DC;
  --mupg-warn-deep: #B86E00;
  --mupg-success: #2E7D32;
  --mupg-success-soft: #E8F5E9;
  --mupg-danger: #C62828;
  --mupg-danger-soft: #FDECEC;
  --mupg-text-1: #111827;
  --mupg-text-2: #374151;
  --mupg-text-3: #6B7280;
  --mupg-text-4: #9CA3AF;
  --mupg-border-1: #ECECEC;
  --mupg-border-2: #DDDDDD;
  --mupg-surface: #FFFFFF;
  --mupg-surface-sub: #FAFAFA;
  --mupg-surface-sub-2: #F4F4F5;
  --mupg-radius-sm: 6px;
  --mupg-radius-md: 8px;
  --mupg-radius-lg: 12px;
  --mupg-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --mupg-shadow-md: 0 4px 16px rgba(15, 23, 42, 0.06);
  --mupg-navbar-h: 60px;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'cv11', 'ss03';
  color: var(--mupg-text-1);
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: -0.005em;
}
.mupg-root * { box-sizing: border-box; }
.mupg-root.mupg-page {
  background: var(--mupg-surface-sub);
  min-height: 100vh;
  padding: 0;
}

/* ----- Sticky top region (page header + filter bar) ----- */
.mupg-sticky-top {
  position: sticky;
  top: var(--mupg-navbar-h);
  z-index: 30;
  background: var(--mupg-surface-sub);
  border-bottom: 1px solid var(--mupg-border-1);
}
.mupg-header {
  padding: 20px 24px 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.mupg-title-block { min-width: 0; flex: 1 1 320px; }
.mupg-title {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0 0 4px;
  color: var(--mupg-text-1);
}
.mupg-subtitle {
  display: flex; align-items: center; gap: 10px;
  font-size: 12.5px; color: var(--mupg-text-3); flex-wrap: wrap;
}
.mupg-subtitle-pill {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--mupg-primary-soft);
  color: #0E5EAB;
  padding: 3px 9px; border-radius: 999px;
  font-size: 11.5px; font-weight: 500;
  border: 1px solid #BFDCF8;
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
}
.mupg-subtitle-pill b { font-weight: 600; }
.mupg-subtitle-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--mupg-text-4); }

/* ----- Action bar / buttons ----- */
.mupg-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.mupg-btn {
  display: inline-flex; align-items: center; gap: 7px;
  height: 34px; padding: 0 14px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  border-radius: var(--mupg-radius-md);
  border: 1px solid transparent; cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.mupg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.mupg-btn-primary { background: var(--mupg-primary); color: #fff; }
.mupg-btn-primary:hover:not(:disabled) { background: #1A7DD9; }
.mupg-btn-danger { background: var(--mupg-danger); color: #fff; }
.mupg-btn-danger:hover:not(:disabled) { background: #B02121; }
.mupg-btn-ghost {
  background: var(--mupg-surface); color: var(--mupg-text-2);
  border-color: var(--mupg-border-2);
}
.mupg-btn-ghost:hover:not(:disabled) { background: var(--mupg-surface-sub-2); }

/* ----- Filter bar ----- */
.mupg-filterbar {
  display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap;
  padding: 14px 24px 16px;
  background: var(--mupg-surface);
  border-top: 1px solid var(--mupg-border-1);
}
.mupg-filter-field { display: flex; flex-direction: column; gap: 4px; }
.mupg-filter-field label {
  font-size: 10.5px; font-weight: 500; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--mupg-text-3);
}
.mupg-input {
  height: 34px; padding: 0 10px;
  background: var(--mupg-surface);
  border: 1px solid var(--mupg-border-2);
  border-radius: var(--mupg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--mupg-text-1);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  min-width: 140px;
}
.mupg-input:focus {
  outline: none;
  border-color: var(--mupg-primary);
  box-shadow: 0 0 0 3px var(--mupg-primary-soft);
}
.mupg-input-search {
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg' fill='none'%3E%3Ccircle cx='7' cy='7' r='4.5' stroke='%236B7280' stroke-width='1.4'/%3E%3Cpath d='M10.5 10.5l3 3' stroke='%236B7280' stroke-width='1.4' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 10px center;
  padding-left: 30px;
  min-width: 240px;
}
.mupg-filter-count {
  margin-left: auto;
  font-size: 11.5px; color: var(--mupg-text-3);
  padding-bottom: 8px;
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
}

/* ----- Body region ----- */
.mupg-body { padding: 18px 24px 32px; }

/* ----- Table card ----- */
.mupg-card {
  background: var(--mupg-surface);
  border: 1px solid var(--mupg-border-1);
  border-radius: var(--mupg-radius-lg);
  box-shadow: var(--mupg-shadow-sm);
  overflow: hidden;
}
.mupg-table-wrap { width: 100%; overflow-x: auto; }
.mupg-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  min-width: 860px;
}
.mupg-table thead th {
  background: var(--mupg-surface);
  color: var(--mupg-text-2);
  font-size: 11px; font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 10px;
  border-bottom: 1px solid var(--mupg-border-2);
  text-align: left;
  white-space: nowrap;
}
.mupg-table thead th.r { text-align: right; }
.mupg-table tbody td {
  padding: 11px 10px;
  border-bottom: 1px solid var(--mupg-border-1);
  vertical-align: middle;
  color: var(--mupg-text-1);
}
.mupg-table tbody tr:last-child td { border-bottom: none; }
.mupg-table tbody tr { transition: background 0.1s ease; }
.mupg-table tbody tr:hover { background: var(--mupg-surface-sub); }
.mupg-table tbody td.mono {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  color: var(--mupg-text-2);
  white-space: nowrap;
}

.mupg-user-cell { display: flex; flex-direction: column; gap: 1px; min-width: 180px; }
.mupg-user-name { font-weight: 500; color: var(--mupg-text-1); font-size: 13px; }

/* Role chips */
.mupg-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px 2px 6px;
  font-size: 11px; font-weight: 500;
  border-radius: 999px;
  white-space: nowrap;
}
.mupg-chip-dot { width: 6px; height: 6px; border-radius: 50%; }
.mupg-chip-admin {
  background: var(--mupg-warn-soft); color: var(--mupg-warn-deep);
  border: 1px solid #F1D88A;
}
.mupg-chip-admin .mupg-chip-dot { background: var(--mupg-warn-deep); }
.mupg-chip-manager {
  background: var(--mupg-primary-soft); color: #0E5EAB;
  border: 1px solid #BFDCF8;
}
.mupg-chip-manager .mupg-chip-dot { background: var(--mupg-primary); }
.mupg-chip-accountant {
  background: var(--mupg-success-soft); color: var(--mupg-success);
  border: 1px solid #B7DBBA;
}
.mupg-chip-accountant .mupg-chip-dot { background: var(--mupg-success); }
.mupg-chip-neutral {
  background: var(--mupg-surface-sub-2); color: var(--mupg-text-2);
  border: 1px solid var(--mupg-border-2);
}
.mupg-chip-neutral .mupg-chip-dot { background: var(--mupg-text-4); }

/* Assigned-account pills */
.mupg-acc-pills { display: flex; flex-wrap: wrap; gap: 4px; max-width: 420px; }
.mupg-acc-pill {
  display: inline-flex; align-items: center;
  padding: 2px 9px;
  background: var(--mupg-surface-sub-2);
  border: 1px solid var(--mupg-border-1);
  border-radius: 999px;
  font-size: 11.5px; color: var(--mupg-text-2);
  white-space: nowrap;
}
.mupg-none { color: var(--mupg-text-4); font-style: italic; font-size: 12.5px; }

/* Row actions */
.mupg-row-actions { display: flex; justify-content: flex-end; align-items: center; }
.mupg-icon-btn {
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--mupg-radius-sm);
  color: var(--mupg-text-3);
  cursor: pointer;
  transition: all 0.12s ease;
}
.mupg-icon-btn:hover {
  background: var(--mupg-surface-sub-2);
  color: var(--mupg-text-1);
  border-color: var(--mupg-border-2);
}

/* ----- Empty / Loading states ----- */
.mupg-state {
  padding: 56px 32px;
  text-align: center;
  color: var(--mupg-text-3);
}
.mupg-state-icon {
  margin: 0 auto 12px;
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: var(--mupg-surface-sub-2);
  color: var(--mupg-text-3);
}
.mupg-state-title { color: var(--mupg-text-2); font-size: 14px; font-weight: 500; margin-bottom: 4px; }
.mupg-state-sub { font-size: 12.5px; color: var(--mupg-text-3); max-width: 360px; margin: 0 auto; }
.mupg-loading {
  padding: 56px 32px;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  color: var(--mupg-text-3); font-size: 12.5px;
}

/* ----- Menu (3-dot + select dropdowns) ----- */
.mupg-menu-paper {
  border-radius: var(--mupg-radius-md) !important;
  border: 1px solid var(--mupg-border-1);
  box-shadow: var(--mupg-shadow-md) !important;
  min-width: 140px;
}
.mupg-root .MuiMenuItem-root {
  font-family: 'Geist', -apple-system, sans-serif !important;
  font-size: 13px !important;
  color: var(--mupg-text-1);
}
.mupg-root .mupg-menu-danger { color: var(--mupg-danger) !important; }

/* ----- Dialog skinning (MUI Dialog underneath, custom look) ----- */
.mupg-dlg .MuiPaper-root {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif !important;
  border-radius: var(--mupg-radius-lg) !important;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18) !important;
  background: var(--mupg-surface);
  overflow: hidden;
}
.mupg-dlg-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--mupg-border-1);
}
.mupg-dlg-title { font-size: 16px; font-weight: 600; color: var(--mupg-text-1); margin: 0; }
.mupg-dlg-sub { font-size: 12px; color: var(--mupg-text-3); margin-top: 3px; }
.mupg-dlg-sub b { font-weight: 600; color: var(--mupg-text-2); }
.mupg-dlg-close {
  background: none; border: none; color: var(--mupg-text-3);
  width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.mupg-dlg-close:hover { background: var(--mupg-surface-sub-2); color: var(--mupg-text-1); }
.mupg-dlg-body { padding: 18px 20px; }
.mupg-dlg-foot {
  padding: 14px 20px;
  border-top: 1px solid var(--mupg-border-1);
  background: var(--mupg-surface-sub);
  display: flex; align-items: center; justify-content: flex-end;
  gap: 8px;
}

/* Dialog error banner */
.mupg-dlg-alert {
  margin-bottom: 14px;
  padding: 10px 14px;
  background: var(--mupg-danger-soft);
  color: var(--mupg-danger);
  border-left: 3px solid var(--mupg-danger);
  border-radius: var(--mupg-radius-sm);
  font-size: 13px;
  line-height: 1.45;
}

/* Dialog form bits */
.mupg-flab {
  display: block; font-size: 11px; font-weight: 500;
  color: var(--mupg-text-2); letter-spacing: 0.02em;
  text-transform: uppercase; margin-bottom: 4px;
}
.mupg-flab-req::after { content: ' *'; color: var(--mupg-primary); font-weight: 700; }
.mupg-fhint { font-size: 11px; color: var(--mupg-text-3); margin-top: 3px; }
.mupg-fhint.is-error { color: var(--mupg-danger); font-weight: 500; }
.mupg-frow { display: grid; gap: 14px; margin-bottom: 14px; grid-template-columns: 1fr; }
.mupg-frow-2 { grid-template-columns: 1fr 1fr; }
@media (max-width: 600px) { .mupg-frow-2 { grid-template-columns: 1fr; } }

.mupg-finput {
  width: 100%; height: 34px; padding: 0 10px;
  border: 1px solid var(--mupg-border-2);
  border-radius: var(--mupg-radius-md);
  font-family: inherit; font-size: 13px;
  color: var(--mupg-text-1); background: #fff;
}
.mupg-finput:focus {
  outline: none; border-color: var(--mupg-primary);
  box-shadow: 0 0 0 3px var(--mupg-primary-soft);
}
.mupg-finput.is-error, .mupg-finput.is-error:focus {
  border-color: var(--mupg-danger);
  box-shadow: 0 0 0 3px var(--mupg-danger-soft);
}

/* Admin-confirmation section box */
.mupg-fsec {
  margin-top: 4px;
  padding: 14px;
  background: var(--mupg-surface-sub);
  border: 1px solid var(--mupg-border-1);
  border-radius: var(--mupg-radius-md);
}
.mupg-fsec-head {
  display: flex; align-items: center; gap: 7px;
  font-size: 12.5px; font-weight: 600;
  color: var(--mupg-text-1);
  margin-bottom: 10px;
}

/* Delete warning banner */
.mupg-delete-warning {
  display: flex; gap: 10px; align-items: flex-start;
  padding: 11px 12px;
  background: var(--mupg-danger-soft);
  border: 1px solid #F4C7C7;
  border-radius: var(--mupg-radius-md);
  margin-bottom: 14px;
  font-size: 12.5px; color: var(--mupg-danger); line-height: 1.5;
}
.mupg-delete-warning svg { flex-shrink: 0; margin-top: 1px; }

/* MUI Select polish inside our dialogs */
.mupg-root .mupg-mui-select {
  font-family: 'Geist', -apple-system, sans-serif !important;
  font-size: 13px !important;
  background: #fff;
  border-radius: var(--mupg-radius-md) !important;
}
.mupg-root .mupg-mui-select .MuiOutlinedInput-notchedOutline {
  border-color: var(--mupg-border-2) !important;
  border-radius: var(--mupg-radius-md) !important;
}
.mupg-root .mupg-mui-select.Mui-focused .MuiOutlinedInput-notchedOutline {
  border-color: var(--mupg-primary) !important;
  border-width: 1px !important;
}
.mupg-root .mupg-mui-select .MuiSelect-select {
  padding-top: 6.5px !important;
  padding-bottom: 6.5px !important;
  min-height: 21px !important;
}
.mupg-select-pills { display: flex; flex-wrap: wrap; gap: 4px; }
.mupg-select-placeholder { color: var(--mupg-text-4); font-size: 13px; }

/* Responsive tightening */
@media (max-width: 640px) {
  .mupg-header { padding: 16px 16px 10px; }
  .mupg-filterbar { padding: 12px 16px 14px; }
  .mupg-body { padding: 14px 12px 28px; }
  .mupg-input-search { min-width: 0; width: 100%; }
  .mupg-filter-field { flex: 1 1 100%; }
}
`;

// ----------------------------------------
// Small inline icons
// ----------------------------------------
function IconPlus({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function IconClose({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconDots({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <circle cx="3" cy="8" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="13" cy="8" r="1.4" />
    </svg>
  );
}
function IconUsers({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="7.5" cy="6.5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M3 15.5c0-2.2 2-4 4.5-4s4.5 1.8 4.5 4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M13.5 9a2.3 2.3 0 100-4.6M14.6 11.6c1.7.5 2.9 1.9 2.9 3.9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}
function IconAlert({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8 1.8L15 14H1L8 1.8z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path d="M8 6.4v3.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="8" cy="12" r="0.75" fill="currentColor" />
    </svg>
  );
}

// ----------------------------------------
// Reusable: role chip
// ----------------------------------------
function RoleChip({ role }) {
  const r = (role || "").toLowerCase();
  let cls = "mupg-chip-neutral";
  if (r === "admin") cls = "mupg-chip-admin";
  else if (r === "manager") cls = "mupg-chip-manager";
  else if (r === "accountant") cls = "mupg-chip-accountant";
  return (
    <span className={`mupg-chip ${cls}`}>
      <span className="mupg-chip-dot" />
      {role || "—"}
    </span>
  );
}

export default function ManageUsersPage() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Main data
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");

  // Presentation-only: first-load spinner (set false after the first fetch
  // resolves; search refetches never re-show it).
  const [loading, setLoading] = useState(true);

  // Alerts
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  // Add user
  const [openAdd, setOpenAdd] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    role: "Manager",
    assignedAccounts: [],
    password: "",
  });
  const [adminPasswordForAdd, setAdminPasswordForAdd] = useState("");
  const [addError, setAddError] = useState("");

  // For quick email check
  const [emailTaken, setEmailTaken] = useState(false);

  // Edit user
  const [openEdit, setOpenEdit] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editAssigned, setEditAssigned] = useState([]);
  const [editRole, setEditRole] = useState("Manager");
  // NEW: New password field for editing a user (optional)
  const [editPassword, setEditPassword] = useState("");
  const [adminPasswordForEdit, setAdminPasswordForEdit] = useState("");
  const [editError, setEditError] = useState("");

  // Delete user
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteAdminPassword, setDeleteAdminPassword] = useState("");
  const [deleteError, setDeleteError] = useState("");

  // 3-dot menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUserId, setMenuUserId] = useState(null);

  // ---------------------------------------------------------------------------
  // Fetch users + accounts
  // ---------------------------------------------------------------------------
  const fetchUsers = useCallback(async () => {
    try {
      const response = await API.get(`/users?search=${search}`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      setAlertMessage("Failed to fetch users");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await API.get("/accounts?limit=1000000000");
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // ---------------------------------------------------------------------------
  // 3-dot menu open/close
  // ---------------------------------------------------------------------------
  const handleMenuOpen = (event, userId) => {
    setAnchorEl(event.currentTarget);
    setMenuUserId(userId);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUserId(null);
  };

  // ---------------------------------------------------------------------------
  // ADD User
  // ---------------------------------------------------------------------------
  const handleOpenAdd = () => {
    setNewUser({
      username: "",
      role: "Manager",
      assignedAccounts: [],
      password: "",
    });
    setAdminPasswordForAdd("");
    setAddError("");
    setEmailTaken(false);
    setOpenAdd(true);
  };
  const handleCloseAdd = () => setOpenAdd(false);

  const handleAddUser = async () => {
    setAddError("");
    if (!adminPasswordForAdd) {
      setAddError("Admin password is required");
      return;
    }
    if (emailTaken) {
      setAddError("The email is already taken. Please pick another.");
      return;
    }
    try {
      await API.post("/users", {
        ...newUser,
        adminPassword: adminPasswordForAdd,
      });
      setAlertMessage("User added successfully!");
      setAlertSeverity("success");
      setOpenAdd(false);
      fetchUsers();
    } catch (error) {
      console.error("Error creating user:", error);
      setAddError(
        error.response?.data?.message || "Failed to create user. Please try again."
      );
    }
  };

  // Quick email check on blur
  const handleEmailBlur = async () => {
    if (!newUser.username) {
      setEmailTaken(false);
      return;
    }
    try {
      const res = await API.get(`/users?search=${newUser.username}`);
      // If we find an exact match for username => emailTaken = true
      const matches = res.data.filter((u) => u.username === newUser.username);
      setEmailTaken(matches.length > 0);
    } catch (err) {
      console.error("Email check error:", err);
    }
  };

  // ---------------------------------------------------------------------------
  // EDIT User
  // ---------------------------------------------------------------------------
  const handleOpenEdit = (userId) => {
    handleMenuClose();
    const target = users.find((u) => u._id === userId);
    if (!target) return;
    setEditUser(target);
    setEditRole(target.role || "Manager");
    setEditAssigned(target.assignedAccounts?.map((acc) => acc._id) || []);
    setEditPassword(""); // reset the new password field
    setAdminPasswordForEdit("");
    setEditError("");
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditUser(null);
  };

  const handleSaveEdit = async () => {
    if (!adminPasswordForEdit) {
      setEditError("Admin password is required");
      return;
    }
    try {
      const payload = {
        adminPassword: adminPasswordForEdit,
        role: editRole,
        assignedAccounts: editAssigned,
      };
      // Include new password if provided (it is optional)
      if (editPassword) {
        payload.password = editPassword;
      }
      await API.put(`/users/${editUser._id}`, payload);
      setAlertMessage("User updated successfully!");
      setAlertSeverity("success");
      setOpenEdit(false);
      fetchUsers();
    } catch (error) {
      console.error("Error editing user:", error);
      setEditError(
        error.response?.data?.message || "Failed to update user. Please try again."
      );
    }
  };

  // ---------------------------------------------------------------------------
  // DELETE User
  // ---------------------------------------------------------------------------
  const handleOpenDelete = (userId) => {
    handleMenuClose();
    setDeleteUserId(userId);
    setDeleteAdminPassword("");
    setDeleteError("");
    setOpenDelete(true);
  };
  const handleCloseDelete = () => {
    setOpenDelete(false);
    setDeleteUserId(null);
  };
  const handleConfirmDelete = async () => {
    if (!deleteAdminPassword) {
      setDeleteError("Admin password is required");
      return;
    }
    try {
      await API.delete(`/users/${deleteUserId}`, {
        data: { adminPassword: deleteAdminPassword },
      });
      setAlertMessage("User deleted successfully!");
      setAlertSeverity("success");
      setOpenDelete(false);
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      setDeleteError(
        error.response?.data?.message || "Failed to delete user. Please try again."
      );
    }
  };

  // Presentation-only: username of the user targeted by the delete dialog
  // (derived from the already-fetched list).
  const deleteTarget = users.find((u) => u._id === deleteUserId);

  // Shared MenuProps so Select dropdowns pick up the scoped skin.
  const selectMenuProps = {
    PaperProps: { className: "mupg-root mupg-menu-paper" },
  };

  // Renders selected account ids as pills inside the multi-selects.
  const renderAccountPills = (selected) => {
    if (!selected || selected.length === 0) {
      return <span className="mupg-select-placeholder">No accounts selected</span>;
    }
    return (
      <span className="mupg-select-pills">
        {selected.map((id) => {
          const acc = accounts.find((a) => a._id === id);
          return (
            <span key={id} className="mupg-acc-pill">
              {acc ? acc.accountName : id}
            </span>
          );
        })}
      </span>
    );
  };

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------
  return (
    <div className="mupg-root mupg-page">
      <style>{MANAGE_USERS_CSS}</style>

      {/* Sticky page-top */}
      <div className="mupg-sticky-top">
        <div className="mupg-header">
          <div className="mupg-title-block">
            <h1 className="mupg-title">Manage Users</h1>
            <div className="mupg-subtitle">
              <span className="mupg-subtitle-pill">
                <b>{users.length}</b>
                {users.length === 1 ? "user" : "users"}
              </span>
              <span className="mupg-subtitle-dot" />
              <span>Team members with access to this organization</span>
            </div>
          </div>

          {/* Action bar */}
          <div className="mupg-actions">
            <button
              type="button"
              className="mupg-btn mupg-btn-primary"
              onClick={handleOpenAdd}
            >
              <IconPlus /> Add User
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="mupg-filterbar">
          <div className="mupg-filter-field">
            <label htmlFor="mupg-search">Search users</label>
            <input
              id="mupg-search"
              className="mupg-input mupg-input-search"
              type="text"
              placeholder="Search by email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="mupg-filter-count">
            {users.length} {users.length === 1 ? "result" : "results"}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="mupg-body">
        <div className="mupg-card">
          {loading ? (
            <div className="mupg-loading">
              <CircularProgress size={22} />
              <span>Loading users…</span>
            </div>
          ) : users.length === 0 ? (
            <div className="mupg-state">
              <div className="mupg-state-icon">
                <IconUsers />
              </div>
              <div className="mupg-state-title">
                {search ? "No users match your search" : "No users yet"}
              </div>
              <div className="mupg-state-sub">
                {search
                  ? "Try a different email or clear the search field."
                  : "Add your first team member with the Add User button."}
              </div>
            </div>
          ) : (
            <div className="mupg-table-wrap">
              <table className="mupg-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Assigned accounts</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th className="r" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    // If user is Admin => no 3-dot menu
                    const hideMenu = user.role === "Admin";
                    const assigned = user.assignedAccounts || [];
                    return (
                      <tr key={user._id}>
                        <td>
                          <div className="mupg-user-cell">
                            <span className="mupg-user-name">{user.username}</span>
                          </div>
                        </td>
                        <td>
                          <RoleChip role={user.role} />
                        </td>
                        <td>
                          {assigned.length > 0 ? (
                            <div className="mupg-acc-pills">
                              {assigned.map((acc) => (
                                <span key={acc._id} className="mupg-acc-pill">
                                  {acc.accountName}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="mupg-none">None</span>
                          )}
                        </td>
                        <td className="mono">
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleString()
                            : "—"}
                        </td>
                        <td className="mono">
                          {user.updatedAt
                            ? new Date(user.updatedAt).toLocaleString()
                            : "—"}
                        </td>
                        <td>
                          <div className="mupg-row-actions">
                            {!hideMenu && (
                              <button
                                type="button"
                                className="mupg-icon-btn"
                                aria-label="User actions"
                                onClick={(e) => handleMenuOpen(e, user._id)}
                              >
                                <IconDots />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 3-dot menu for Edit + Delete */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ className: "mupg-root mupg-menu-paper" }}
      >
        <MenuItem onClick={() => handleOpenEdit(menuUserId)}>Edit</MenuItem>
        <MenuItem
          className="mupg-menu-danger"
          onClick={() => handleOpenDelete(menuUserId)}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Feedback snackbar (was a top-of-page Alert; same state drives it) */}
      <Snackbar
        open={Boolean(alertMessage)}
        autoHideDuration={4000}
        onClose={() => setAlertMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlertMessage("")}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* ================================================ */}
      {/* ADD USER DIALOG                                   */}
      {/* ================================================ */}
      <Dialog
        open={openAdd}
        onClose={handleCloseAdd}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmallScreen}
        className="mupg-dlg"
        PaperProps={{ className: "mupg-root" }}
      >
        <style>{MANAGE_USERS_CSS}</style>
        <div className="mupg-dlg-head">
          <div>
            <div className="mupg-dlg-title">Add User</div>
            <div className="mupg-dlg-sub">
              Create a team member account for this organization.
            </div>
          </div>
          <button
            className="mupg-dlg-close"
            onClick={handleCloseAdd}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent dividers className="mupg-dlg-body">
          {addError && <div className="mupg-dlg-alert">{addError}</div>}

          <div className="mupg-frow mupg-frow-2">
            <div>
              <label className="mupg-flab mupg-flab-req" htmlFor="mupg-add-email">
                Username (Email)
              </label>
              <input
                id="mupg-add-email"
                className={`mupg-finput ${emailTaken ? "is-error" : ""}`}
                type="email"
                placeholder="name@company.com"
                value={newUser.username}
                onBlur={handleEmailBlur}
                onChange={(e) =>
                  setNewUser({ ...newUser, username: e.target.value })
                }
              />
              {emailTaken ? (
                <div className="mupg-fhint is-error">
                  This email is already taken.
                </div>
              ) : (
                <div className="mupg-fhint">
                  Checked for duplicates when you leave the field.
                </div>
              )}
            </div>
            <div>
              <label className="mupg-flab mupg-flab-req" htmlFor="mupg-add-pass">
                Password
              </label>
              <input
                id="mupg-add-pass"
                className="mupg-finput"
                type="password"
                placeholder="Initial password"
                value={newUser.password}
                onChange={(e) =>
                  setNewUser({ ...newUser, password: e.target.value })
                }
              />
            </div>
          </div>

          <div className="mupg-frow">
            <div>
              <label className="mupg-flab">Role</label>
              <Select
                className="mupg-mui-select"
                fullWidth
                size="small"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                MenuProps={selectMenuProps}
              >
                <MenuItem value="Manager">Manager</MenuItem>
                <MenuItem value="Accountant">Accountant</MenuItem>
                <MenuItem value="Staff">Staff</MenuItem>
              </Select>
            </div>

            {/* Assigned Accounts (multiple) */}
            <div>
              <label className="mupg-flab">Assigned Accounts</label>
              <Select
                className="mupg-mui-select"
                fullWidth
                size="small"
                multiple
                displayEmpty
                value={newUser.assignedAccounts}
                onChange={(e) =>
                  setNewUser({
                    ...newUser,
                    assignedAccounts: e.target.value,
                  })
                }
                renderValue={renderAccountPills}
                MenuProps={selectMenuProps}
              >
                {accounts.map((acc) => (
                  <MenuItem key={acc._id} value={acc._id}>
                    {acc.accountName}
                  </MenuItem>
                ))}
              </Select>
              <div className="mupg-fhint">
                Which accounts this user can work with.
              </div>
            </div>
          </div>

          {/* Admin Password */}
          <div className="mupg-fsec">
            <div className="mupg-fsec-head">Admin confirmation</div>
            <label className="mupg-flab mupg-flab-req" htmlFor="mupg-add-admin">
              Admin Password
            </label>
            <input
              id="mupg-add-admin"
              className="mupg-finput"
              type="password"
              placeholder="Re-enter your admin password"
              value={adminPasswordForAdd}
              onChange={(e) => setAdminPasswordForAdd(e.target.value)}
            />
            <div className="mupg-fhint">
              Required to authorise creating a user.
            </div>
          </div>
        </DialogContent>
        <div className="mupg-dlg-foot">
          <button
            type="button"
            className="mupg-btn mupg-btn-ghost"
            onClick={handleCloseAdd}
          >
            Cancel
          </button>
          <button
            type="button"
            className="mupg-btn mupg-btn-primary"
            onClick={handleAddUser}
          >
            <IconPlus /> Add
          </button>
        </div>
      </Dialog>

      {/* ================================================ */}
      {/* EDIT USER DIALOG                                  */}
      {/* ================================================ */}
      <Dialog
        open={openEdit}
        onClose={handleCloseEdit}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmallScreen}
        className="mupg-dlg"
        PaperProps={{ className: "mupg-root" }}
      >
        <style>{MANAGE_USERS_CSS}</style>
        <div className="mupg-dlg-head">
          <div>
            <div className="mupg-dlg-title">Edit User</div>
            {editUser && (
              <div className="mupg-dlg-sub">
                Editing <b>{editUser.username}</b>
              </div>
            )}
          </div>
          <button
            className="mupg-dlg-close"
            onClick={handleCloseEdit}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent dividers className="mupg-dlg-body">
          {editError && <div className="mupg-dlg-alert">{editError}</div>}
          {editUser && (
            <>
              <div className="mupg-frow">
                <div>
                  <label className="mupg-flab">Role</label>
                  <Select
                    className="mupg-mui-select"
                    fullWidth
                    size="small"
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="Manager">Manager</MenuItem>
                    <MenuItem value="Accountant">Accountant</MenuItem>
                    <MenuItem value="Staff">Staff</MenuItem>
                  </Select>
                </div>

                <div>
                  <label className="mupg-flab">Assigned Accounts</label>
                  <Select
                    className="mupg-mui-select"
                    fullWidth
                    size="small"
                    multiple
                    displayEmpty
                    value={editAssigned}
                    onChange={(e) => setEditAssigned(e.target.value)}
                    renderValue={renderAccountPills}
                    MenuProps={selectMenuProps}
                  >
                    {accounts.map((acc) => (
                      <MenuItem key={acc._id} value={acc._id}>
                        {acc.accountName}
                      </MenuItem>
                    ))}
                  </Select>
                  <div className="mupg-fhint">
                    Which accounts this user can work with.
                  </div>
                </div>

                {/* NEW: Optional field for updating the user's password */}
                <div>
                  <label className="mupg-flab" htmlFor="mupg-edit-pass">
                    New Password (optional)
                  </label>
                  <input
                    id="mupg-edit-pass"
                    className="mupg-finput"
                    type="password"
                    placeholder="••••••••"
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                  />
                  <div className="mupg-fhint">
                    Leave blank to keep current password
                  </div>
                </div>
              </div>

              <div className="mupg-fsec">
                <div className="mupg-fsec-head">Admin confirmation</div>
                <label
                  className="mupg-flab mupg-flab-req"
                  htmlFor="mupg-edit-admin"
                >
                  Admin Password
                </label>
                <input
                  id="mupg-edit-admin"
                  className="mupg-finput"
                  type="password"
                  placeholder="Re-enter your admin password"
                  value={adminPasswordForEdit}
                  onChange={(e) => setAdminPasswordForEdit(e.target.value)}
                />
                <div className="mupg-fhint">
                  Required to authorise updating this user.
                </div>
              </div>
            </>
          )}
        </DialogContent>
        <div className="mupg-dlg-foot">
          <button
            type="button"
            className="mupg-btn mupg-btn-ghost"
            onClick={handleCloseEdit}
          >
            Cancel
          </button>
          <button
            type="button"
            className="mupg-btn mupg-btn-primary"
            onClick={handleSaveEdit}
          >
            Update
          </button>
        </div>
      </Dialog>

      {/* ================================================ */}
      {/* DELETE USER DIALOG                                */}
      {/* ================================================ */}
      <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        fullWidth
        maxWidth="xs"
        className="mupg-dlg"
        PaperProps={{ className: "mupg-root" }}
      >
        <style>{MANAGE_USERS_CSS}</style>
        <div className="mupg-dlg-head">
          <div>
            <div className="mupg-dlg-title">Delete User</div>
            {deleteTarget && (
              <div className="mupg-dlg-sub">
                <b>{deleteTarget.username}</b>
              </div>
            )}
          </div>
          <button
            className="mupg-dlg-close"
            onClick={handleCloseDelete}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent dividers className="mupg-dlg-body">
          {deleteError && <div className="mupg-dlg-alert">{deleteError}</div>}
          <div className="mupg-delete-warning">
            <IconAlert />
            <span>Are you sure you want to delete this user?</span>
          </div>
          <label className="mupg-flab mupg-flab-req" htmlFor="mupg-del-admin">
            Admin Password
          </label>
          <input
            id="mupg-del-admin"
            className="mupg-finput"
            type="password"
            placeholder="Re-enter your admin password"
            value={deleteAdminPassword}
            onChange={(e) => setDeleteAdminPassword(e.target.value)}
          />
          <div className="mupg-fhint">
            Required to authorise deleting this user.
          </div>
        </DialogContent>
        <div className="mupg-dlg-foot">
          <button
            type="button"
            className="mupg-btn mupg-btn-ghost"
            onClick={handleCloseDelete}
          >
            Cancel
          </button>
          <button
            type="button"
            className="mupg-btn mupg-btn-danger"
            onClick={handleConfirmDelete}
          >
            Delete
          </button>
        </div>
      </Dialog>
    </div>
  );
}
